import { useRef, useState } from 'react'

// Chat attachments are sent to the LLM on every message (and stored in workspace_message),
// unlike the one-time authored exercise images in GuiExerciseEditor.jsx - so these need to
// be meaningfully smaller than that flow's 1000px/0.7 constants. This adaptively steps
// quality down, then width, until the encoded size clears the budget or the floor is hit,
// rather than a single fixed setting that could still balloon on a busy/high-contrast crop.
const CHAT_IMAGE_MAX_WIDTH = 900
const CHAT_IMAGE_MIN_WIDTH = 400
const CHAT_IMAGE_START_QUALITY = 0.6
const CHAT_IMAGE_MIN_QUALITY = 0.3
const CHAT_IMAGE_TARGET_BYTES = 300 * 1024 // budget on the data URL string length

function compressRegion(img, sx, sy, sw, sh) {
  let targetWidth = Math.min(CHAT_IMAGE_MAX_WIDTH, sw)
  let quality = CHAT_IMAGE_START_QUALITY
  let dataUrl = null
  for (let attempt = 0; attempt < 6; attempt += 1) {
    const scale = targetWidth / sw
    const outW = Math.max(1, Math.round(sw * scale))
    const outH = Math.max(1, Math.round(sh * scale))
    const canvas = document.createElement('canvas')
    canvas.width = outW
    canvas.height = outH
    const ctx = canvas.getContext('2d')
    ctx.drawImage(img, sx, sy, sw, sh, 0, 0, outW, outH)
    dataUrl = canvas.toDataURL('image/jpeg', quality)
    const atFloor = targetWidth <= CHAT_IMAGE_MIN_WIDTH && quality <= CHAT_IMAGE_MIN_QUALITY
    if (dataUrl.length <= CHAT_IMAGE_TARGET_BYTES || atFloor) break
    if (quality > CHAT_IMAGE_MIN_QUALITY) {
      quality = Math.max(CHAT_IMAGE_MIN_QUALITY, quality - 0.15)
    } else {
      targetWidth = Math.max(CHAT_IMAGE_MIN_WIDTH, Math.round(targetWidth * 0.75))
    }
  }
  return dataUrl
}

// Lets the student crop the whole-workspace capture down to just the part relevant to their
// question before it's attached - "how much/where on screen is captured" per the original
// ask - rather than always sending the entire workspace (which may include the Target pane,
// unrelated chat history, etc). Leaving no selection attaches the full capture.
export default function ScreenshotRegionSelector({ open, imageDataUrl, onCancel, onConfirm }) {
  const imgRef = useRef(null)
  const [drag, setDrag] = useState(null)
  const [isDragging, setIsDragging] = useState(false)

  if (!open || !imageDataUrl) return null

  const getRelativePoint = (e) => {
    const rect = imgRef.current.getBoundingClientRect()
    return {
      x: Math.min(Math.max(e.clientX - rect.left, 0), rect.width),
      y: Math.min(Math.max(e.clientY - rect.top, 0), rect.height),
    }
  }

  const handleMouseDown = (e) => {
    const p = getRelativePoint(e)
    setIsDragging(true)
    setDrag({ startX: p.x, startY: p.y, x: p.x, y: p.y, w: 0, h: 0 })
  }

  const handleMouseMove = (e) => {
    if (!isDragging || !drag) return
    const p = getRelativePoint(e)
    setDrag({
      ...drag,
      x: Math.min(drag.startX, p.x),
      y: Math.min(drag.startY, p.y),
      w: Math.abs(p.x - drag.startX),
      h: Math.abs(p.y - drag.startY),
    })
  }

  const handleMouseUp = () => setIsDragging(false)
  const handleClearSelection = () => setDrag(null)

  const hasSelection = drag && drag.w > 8 && drag.h > 8

  const handleConfirm = () => {
    const img = imgRef.current
    const rect = img.getBoundingClientRect()
    const scaleX = img.naturalWidth / rect.width
    const scaleY = img.naturalHeight / rect.height

    let sx = 0
    let sy = 0
    let sw = img.naturalWidth
    let sh = img.naturalHeight
    if (hasSelection) {
      sx = Math.round(drag.x * scaleX)
      sy = Math.round(drag.y * scaleY)
      sw = Math.round(drag.w * scaleX)
      sh = Math.round(drag.h * scaleY)
    }

    onConfirm(compressRegion(img, sx, sy, sw, sh))
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: '#ffffff', borderRadius: '8px', padding: '16px', maxWidth: '92vw', maxHeight: '92vh', display: 'flex', flexDirection: 'column', gap: '10px' }}>
        <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#171717' }}>
          Drag to select the part of your workspace to attach, or attach the whole capture.
        </div>
        <div
          style={{ position: 'relative', overflow: 'hidden', cursor: 'crosshair', lineHeight: 0 }}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
        >
          <img
            ref={imgRef}
            src={imageDataUrl}
            alt="Captured workspace"
            draggable={false}
            style={{ display: 'block', maxWidth: '86vw', maxHeight: '64vh', userSelect: 'none' }}
          />
          {drag && drag.w > 0 && drag.h > 0 && (
            <div
              style={{
                position: 'absolute',
                left: drag.x,
                top: drag.y,
                width: drag.w,
                height: drag.h,
                border: '2px solid #2563eb',
                backgroundColor: 'rgba(37, 99, 235, 0.15)',
                pointerEvents: 'none',
              }}
            />
          )}
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <button
            type="button"
            onClick={handleClearSelection}
            disabled={!drag}
            style={{
              fontSize: '0.78rem', padding: '6px 10px', border: '1px solid #e5e5e5', borderRadius: '6px',
              background: '#ffffff', cursor: drag ? 'pointer' : 'default', opacity: drag ? 1 : 0.5,
            }}
          >
            Clear selection
          </button>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              type="button"
              onClick={onCancel}
              style={{ fontSize: '0.78rem', padding: '6px 14px', border: '1px solid #e5e5e5', borderRadius: '6px', background: '#ffffff', cursor: 'pointer' }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              style={{ fontSize: '0.78rem', padding: '6px 14px', border: 'none', borderRadius: '6px', background: '#2563eb', color: '#ffffff', fontWeight: 600, cursor: 'pointer' }}
            >
              Attach {hasSelection ? 'Selection' : 'Full Image'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
